import { type ReactNode } from 'react';

import PageWrapper from '@/components/molecules/PageWrappers/PageWrapper';

const Section = ({ id, title, children }: { id: string; title: string; children: ReactNode }) => (
  <section id={id} className="border-b border-gray-100 pb-6">
    <div className="flex flex-col gap-3 py-6">
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      {children}
    </div>
  </section>
);

export const V2Announcement = () => {
  return (
    <PageWrapper className="bg-gray-50">
      <div className="flex w-full flex-col items-start justify-start px-4 md:px-6 lg:px-8">
        <div className="mb-8 flex w-full flex-col gap-6">
          {/* Header */}
          <div className="w-full rounded-xl bg-white p-8 shadow-sm">
            <div className="flex flex-col gap-4">
              <h1 className="text-3xl font-bold text-gray-900">Wexler 2.0 coming soon!</h1>
              <p className="text-lg text-gray-700">
                We're excited to introduce Wexler 2.0, bringing a more flexible and efficient way to work with your
                documents.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full rounded-xl bg-white p-8 shadow-sm">
            <div className="flex flex-col gap-8">
              {/* What's changing? */}
              <Section id="whats-changing" title="What's changing?">
                <div className="flex flex-col gap-4 text-gray-700">
                  <div>
                    <p className="font-semibold">
                      All the documents contained in your 'Cases' now also sit for analysis at the 'Matter' level:
                    </p>
                    <p className="mt-2">
                      Previously, you uploaded documents to individual Cases within a Matter. Now, you upload all
                      documents directly to the Matter itself and batch them to a 'Subset'. This means your documents
                      are processed only once and automatically deduplicated across an entire Matter.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">'Cases' are now called 'Subsets':</p>
                    <p className="mt-2">
                      Instead of Cases, you'll now work with 'Subsets' (which are collections/batches of documents
                      within your Matter). For example, you might create a Subset for e.g. all deposition transcripts
                      only, or John Doe's expert reports. The functionality you had with your Cases (chronologies,
                      inconsistencies, KiM, etc.) still exists in each Subset.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Work at any level you need:</p>
                    <p className="mt-2">
                      The real power of Wexler 2.0 is flexibility to review some or all of the documents across your
                      Matter.
                    </p>
                  </div>
                </div>
              </Section>

              {/* Wexler 2.0 means you can now */}
              <Section id="what-you-can-do" title="Wexler 2.0 means you can now:">
                <ul className="ml-6 flex list-disc flex-col gap-2 text-gray-700">
                  <li>Analyse all documents across your entire Matter.</li>
                  <li>Focus on specific Subsets when you need a narrower scope.</li>
                  <li>Control your 'Case Issues' at both the Matter and Subset level.</li>
                </ul>
              </Section>

              {/* What about my existing Matters? */}
              <Section id="existing-matters" title="What about my existing Matters?">
                <div className="flex flex-col gap-4 text-gray-700">
                  <p>You don't need to do anything. Migration is handled automatically:</p>
                  <ul className="ml-6 flex list-disc flex-col gap-2">
                    <li>All your Cases are renamed Subsets.</li>
                    <li>All documents from your existing Cases (now called Subsets) will move to the Matter level.</li>
                    <li>
                      All your work product in those Cases (chronologies, KiM answers, inconsistency reports) will
                      remain in the new Subsets and won't be altered or deleted. Continue working by simply opening that
                      Subset.
                    </li>
                  </ul>
                </div>
              </Section>

              {/* Plus more! */}
              <Section id="plus-more" title="Plus more!">
                <p className="text-gray-700">
                  Beyond this structural improvement, we've also included numerous UX and performance upgrades to make
                  your experience faster and smoother.
                </p>
              </Section>

              {/* Contact */}
              <div className="rounded-lg bg-blue-50 p-6">
                <p className="text-gray-700">
                  If you have any questions about these changes, please don't hesitate to reach out to our team at{' '}
                  <a href="mailto:support@wexler.ai" className="font-semibold text-blue-600 hover:text-blue-700">
                    support@wexler.ai
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};
